/**
 * Extract a human-readable **string** from an Axios/API error.
 *
 * Critically, this must ALWAYS return a string. FastAPI request-validation
 * failures (HTTP 422) return `detail` as a *list* of `{loc, msg, type}` objects
 * (and other errors may make it an object). Rendering a non-string in a toast
 * throws React's "Objects are not valid as a React child" and white-screens the
 * app, which is worse than the original error. So we normalise every shape here.
 */
export const errMsg = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;

  if (typeof detail === 'string' && detail.trim()) return detail;

  // FastAPI 422 validation errors: [{ loc: [...], msg, type }, ...]
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d: any) => {
        if (typeof d === 'string') return d;
        const loc = Array.isArray(d?.loc)
          ? d.loc.filter((x: any) => x !== 'body').join('.')
          : '';
        const msg = typeof d?.msg === 'string' ? d.msg : '';
        return loc && msg ? `${loc}: ${msg}` : msg || loc;
      })
      .filter(Boolean);
    if (parts.length) return parts.join('; ');
  }

  if (detail && typeof detail === 'object') {
    if (typeof detail.msg === 'string') return detail.msg;
    try {
      return JSON.stringify(detail);
    } catch {
      /* fall through */
    }
  }

  if (typeof e?.message === 'string' && e.message) return e.message;
  return fallback;
};
